import { GridWall, Heading, Loader, Text, useIsTheme, Theme } from '@afterpaytouch/core'
import clsx from 'clsx'
import { TFunction } from 'next-i18next'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent } from 'react'
import { PostCheckoutFlow } from '../../model'
import { useCheckoutMode, useIsApplePaylater, useIsBillingAgreementApprovalProcess, useMerchantDisplayNameOrName } from '../../state'
import { usePersonalization } from '../../utils/personalization'
import { useGetLocaleWithFallback } from '../../utils/post-checkout'

import styles from './style.module.css'
import { useIsSupFlow } from './ProcessOrder.hooks'

export const ProcessOrder: FunctionComponent = () => {
  const { t } = useTranslation()
  const checkoutMode = useCheckoutMode()
  const merchantName = useMerchantDisplayNameOrName()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const isApplePaylater = useIsApplePaylater()
  const {
    postCheckout: {
      flow,
      showComponentState: { showComponent: showPostCheckout },
    },
  } = usePersonalization()
  const isPreCapture = flow === PostCheckoutFlow.PreCapture
  const isPreCaptureFlow = isPreCapture && showPostCheckout
  // Add margin for headings without the loader
  const headingStyle = clsx(styles.contentWrapper, isPreCaptureFlow && styles.marginTop)
  const isSupFlow = useIsSupFlow()
  const isPegasusTheme = useIsTheme(Theme.Pegasus)
  const isKelpieTheme = useIsTheme(Theme.Kelpie)
  const isPegasusOrKelpieTheme = isPegasusTheme || isKelpieTheme

  const localeWithFallback = useGetLocaleWithFallback('processing')

  const defaultLocale = (item: string): string => {
    if (isApplePaylater) {
      return `processing:applePaylater.${item}`
    }
    if (isBillingAgreementApprovalProcess) {
      return `processing:billingAgreementApproval.${item}`
    }
    if (isSupFlow) {
      return `processing:supOrder.${item}`
    }

    return `processing:${item}`
  }

  const renderProcessingText = (t: TFunction, type: 'heading' | 'description' | 'instruction'): string => {
    const translation = isPreCaptureFlow ? localeWithFallback(type) : defaultLocale(type)

    return t(translation, {
      ...(type === 'description' && !isPreCaptureFlow && { context: checkoutMode.toLowerCase() }),
      merchantName,
      defaultValue: '',
    })
  }

  const description = renderProcessingText(t, 'description')
  const instruction = renderProcessingText(t, 'instruction')

  return (
    <>
      {!isPreCaptureFlow && <Loader testNameSpace='processing-order' />}
      <div className={headingStyle}>
        <Heading testNameSpace='process-order-heading' size={isPegasusOrKelpieTheme ? 'S' : 'XL'} color={isPegasusOrKelpieTheme ? 'Prominent' : undefined}>
          {renderProcessingText(t, 'heading')}
        </Heading>
      </div>
      {(description || instruction) && (
        <div className={styles.contentWrapper}>
          <GridWall columns={1} gutter={'small'}>
            {description && (
              <Text testNameSpace='process-order-description' size='S' color={isPegasusOrKelpieTheme ? 'Subtle' : undefined}>
                {description}
              </Text>
            )}
            {instruction && (
              <Text testNameSpace='process-order-instruction' size='S' color={isPegasusOrKelpieTheme ? 'Prominent' : undefined} bold>
                {instruction}
              </Text>
            )}
          </GridWall>
        </div>
      )}
    </>
  )
}
